import React from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Button from '../../components/form/button/Button';
import Input from '../../components/form/input/Input';
import Icon from '../../components/icon/Icon';
import Header from '../components/header/Header';
import { login, loginExternal, silentLogin } from '../../core/state/auth/asyncActions';
import { LoginFormData } from '../../core/state/auth/types';
import { internalDomainNames } from '../../core/constants/domains';
import { AuthStatus } from '../../core/enums/AuthStatus';
import { HOME_ROUTE } from '../../core/constants/routes';
import { RootState } from '../../core/state/state';
import { spawnGlobalNotification } from '../../core/state/global-notifications/asyncActions';
import AuthService from '../../core/services/AccountService';
import Image from '../../components/image/Image';
import logo from '../../../img/login-logo.svg';
import { createErrorNotificationContent } from './LoginErrorMessage';
import { tokenRequest } from '../../core/constants/login/loginConfig';

import './Login.scss';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const status = useSelector((state: RootState) => state.auth.status);
  const [isExternalForm, setIsExternalForm] = React.useState(false);
  const projectId = useSelector((state: RootState) => state.user.userInfo?.projectID);
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const { instance, inProgress, accounts } = useMsal();
  const [apiData, setApiData] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!apiData && inProgress === InteractionStatus.None && accounts.length > 0) {
      const accessTokenRequest = {
        ...tokenRequest,
        account: accounts[0]
      };

      instance
        .acquireTokenSilent(accessTokenRequest)
        .then(async (accessTokenResponse) => {
          setApiData(accessTokenResponse.accessToken);
          await dispatch(silentLogin());
        })
        .catch(() => undefined);
    }
  }, [instance, accounts, inProgress, dispatch, apiData]);

  const validationSchema = yup
    .object()
    .shape({
      email: yup.string().required(t('validation.email.required')).email(t('validation.email.invalidFormat')).min(2),
      password: yup.string()
    })
    .required();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    },
    resolver: yupResolver(validationSchema)
  });

  React.useEffect(() => {
    const email = query.get('email');
    if (email) {
      setValue('email', email);
      setIsExternalForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const email = watch('email');
  const password = watch('password');

  if (status === AuthStatus.Authenticated && typeof projectId === 'number') {
    return <Redirect to={`/projects/${projectId}/bookings`} />;
  }
  if (status === AuthStatus.Authenticated && typeof projectId !== 'number') {
    return <Redirect to={HOME_ROUTE} />;
  }

  if (inProgress === InteractionStatus.HandleRedirect || apiData) {
    return null;
  }

  const onSubmit = handleSubmit(async (data: LoginFormData) => {
    const isInternalUser = internalDomainNames.some((domain) => email.includes(domain));

    if (isInternalUser) {
      dispatch(login());
    } else if (!isInternalUser && !password) {
      const response = await AuthService.checkEmail(data.email);

      if (!response.data.result.result) {
        dispatch(spawnGlobalNotification(createErrorNotificationContent(response, t)));
      } else {
        setIsExternalForm(true);
        clearErrors('email');
      }
    } else {
      dispatch(loginExternal(data, t));
    }
  });

  const resetForm = () => {
    setValue('password', '');
    setIsExternalForm(false);
  };

  return (
    <div className="login">
      <Header />
      <div className="login__content">
        <form className="login__form" onSubmit={onSubmit}>
          <div className="d-flex justify-content-center">
            <Image src={logo} alt="logo" />
          </div>
          <div className="login__form-title">{t('login.title')}</div>
          {!isExternalForm ? (
            <>
              <Controller
                name="email"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="string"
                    inputMode="email"
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    id="email"
                    error={errors.email?.message}
                    name="email"
                    label={t('login.field.email.label')}
                    placeholder={t('login.field.email.placeholder')}
                    value={value}
                  />
                )}
              />
              <div className="login__footer">
                <Button type="submit" className="login__button" buttonTypes={['blue', 'small']}>
                  <b>{t('common.next')}</b>
                  <Icon className="login__button-icon" icon="arrowRight" size="extra-small" />
                </Button>
              </div>
            </>
          ) : (
            <>
              <div />
              <Controller
                name="password"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="string"
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    id="password"
                    error={errors.password?.message}
                    name="password"
                    label={t('login.field.password.label')}
                    placeholder={t('login.field.password.placeholder')}
                    value={value}
                  />
                )}
              />
              <div className="login__footer d-flex justify-content-between">
                <Button className="login__button" onClick={() => resetForm()} buttonTypes={['blue-outline', 'small']}>
                  <Icon className="login__button-icon-back" icon="arrow" size="extra-small" svg />
                  {t('common.back')}
                </Button>
                <Button className="login__button" buttonTypes={['blue', 'small']} type="submit">
                  <b>{t('common.login')}</b>
                  <Icon className="login__button-icon" icon="arrowRight" size="extra-small" />
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
